import { useState } from 'react';
import { Avatar, Container, Icon, IconButton, IconName, Image, Stack } from '@optimus-web/core';
import { lt, useFeatureFlag } from '@mews/framework';
import {
    PortfolioCentralProductGetDetailResult,
    PortfolioEnterpriseProductGetDetailResult,
} from '@mews-commander/data-access';
import {
    Card,
    ContentSection,
    DividerList,
    LabeledListItem,
    LocalizedDateTimeFromUtc,
    TooltippedSpan,
    Select,
} from '@mews-commander/ui';
import { useCommanderSelector, useGetAvatarSrc } from '@mews-commander/utils';
import { UnicodeEntity } from '@mews/enums';
import { getLanguageOptions } from '@mews-commander/legacy-entities/src/ducks/languages';
import { FeatureFlag } from '@mews-commander/enums';
import { LocalizedProductDetails } from '../../utils/transformLocalizedProductDetails';
import { useEditEnterpriseProductDialogForm } from '../LocalProductSidePanel/forms/EditProductDetailsForm';

export type CentralProductDetailsProps = Omit<PortfolioCentralProductGetDetailResult, 'name'> &
    Partial<Pick<PortfolioCentralProductGetDetailResult, 'name'>> &
    Partial<PortfolioEnterpriseProductGetDetailResult> & {
        lockedFields?: boolean;
        productKey?: string | null;
        hasLanguagesSelect?: boolean;
        localizedData?: LocalizedProductDetails;
        propertyId?: string;
        enterpriseProductId?: string;
    };

export const CentralProductDetails = (props: CentralProductDetailsProps) => {
    const getImgSrc = useGetAvatarSrc();

    const {
        createdUtc,
        creatorProfile,
        description,
        externalIdentifier,
        externalName,
        hasLanguagesSelect,
        imageId,
        localizedData,
        key,
        lockedFields,
        name,
        ordering,
        productKey,
        shortName,
        updatedUtc,
        updaterProfile,
        propertyId,
        enterpriseProductId,
    } = props;

    const centralProductsEditEnabled = useFeatureFlag(FeatureFlag.centralProductsEditEnabled);

    const productImgSrc = getImgSrc(imageId);
    const creatorImgSrc = getImgSrc(creatorProfile.imageId);
    const updaterImgSrc = getImgSrc(updaterProfile.imageId);

    const applicableLanguages = Object.keys(localizedData ?? {});

    const [selectedLanguage, setSelectedLanguage] = useState(applicableLanguages[0]);

    const languageOptions = useCommanderSelector(getLanguageOptions);
    const languageList = languageOptions.filter(({ value }) => applicableLanguages.includes(value || ''));

    const {
        externalName: localizedExternalName,
        description: localizedDescription,
        shortName: localizedShortName,
        name: localizedName,
    } = localizedData?.[selectedLanguage] || {};

    const [renderEditProductDetailsForm, toggleEditProductDetailsForm] = useEditEnterpriseProductDialogForm({
        enterpriseProductId: enterpriseProductId ?? '',
        propertyId: propertyId ?? '',
        productData: props,
    });

    return (
        <>
            <ContentSection
                title={lt('Details')}
                rightContent={
                    hasLanguagesSelect ? (
                        <Stack spacing="100">
                            <Container minWidth="240px">
                                <Select
                                    required
                                    selected={selectedLanguage}
                                    options={languageList}
                                    onChange={setSelectedLanguage}
                                    readOnly={applicableLanguages.length === 1}
                                    size="small"
                                    inline
                                />
                            </Container>
                            {centralProductsEditEnabled && (
                                <TooltippedSpan tooltip={lt('Edit')}>
                                    <IconButton
                                        size="small"
                                        icon={IconName.Edit}
                                        type="button"
                                        onClick={toggleEditProductDetailsForm}
                                    />
                                </TooltippedSpan>
                            )}
                        </Stack>
                    ) : null
                }
            >
                <DividerList>
                    <LabeledListItem
                        data-test-id="internalName"
                        label={lt('InternalName')}
                        itemAlignment="start"
                        contentOptions={{ wordBreak: 'break-all', overflowWrap: 'break-word' }}
                    >
                        <TooltippedSpan tooltip={lockedFields ? lt('ProductLockedFieldTooltip') : null}>
                            <Stack itemAlignment="center" spacing="50">
                                {name || localizedName}
                                {lockedFields && <Icon value={IconName.Lock} size="small" />}
                            </Stack>
                        </TooltippedSpan>
                    </LabeledListItem>
                    <LabeledListItem data-test-id="shortName" label={lt('ShortName')}>
                        {shortName || localizedShortName || UnicodeEntity.Dash}
                    </LabeledListItem>
                    <LabeledListItem data-test-id="ordering" label={lt('Ordering')}>
                        {ordering}
                    </LabeledListItem>
                    <LabeledListItem
                        data-test-id="externalName"
                        label={lt('ExternalName')}
                        itemAlignment="start"
                        contentOptions={{ wordBreak: 'break-all', overflowWrap: 'break-word' }}
                    >
                        {externalName || localizedExternalName || UnicodeEntity.Dash}
                    </LabeledListItem>
                    <LabeledListItem
                        vertical={!!(description || localizedDescription)}
                        data-test-id="description"
                        label={lt('Description')}
                        itemAlignment="start"
                        contentOptions={{ overflowWrap: 'break-word' }}
                    >
                        {description || localizedDescription || UnicodeEntity.Dash}
                    </LabeledListItem>
                </DividerList>
            </ContentSection>
            <ContentSection title={lt('Mapping')}>
                <DividerList>
                    <LabeledListItem
                        data-test-id="productKey"
                        label={lt('ProductKey')}
                        itemAlignment="start"
                        contentOptions={{ wordBreak: 'break-all', overflowWrap: 'break-word' }}
                    >
                        <TooltippedSpan tooltip={lockedFields ? lt('ProductLockedFieldTooltip') : null}>
                            <Stack itemAlignment="center" spacing="50">
                                {productKey || key || UnicodeEntity.Dash}
                                {lockedFields && <Icon value={IconName.Lock} size="small" />}
                            </Stack>
                        </TooltippedSpan>
                    </LabeledListItem>
                    <LabeledListItem data-test-id="externalIdentifier" label={lt('ExternalIdentifier')}>
                        {externalIdentifier || UnicodeEntity.Dash}
                    </LabeledListItem>
                </DividerList>
            </ContentSection>
            {productImgSrc && (
                <ContentSection title={lt('ProductImage')}>
                    <Card outline>
                        <Image src={productImgSrc} imageRatio="16/9" />
                    </Card>
                </ContentSection>
            )}
            <ContentSection title={lt('ActivityLog')}>
                <DividerList>
                    <LabeledListItem data-test-id="createdBy" label={lt('CreatedBy')} itemAlignment="center">
                        <Stack itemAlignment="center" spacing="200">
                            <Avatar src={creatorImgSrc} size="medium" title={creatorProfile?.name} />
                            {creatorProfile?.name}
                        </Stack>
                    </LabeledListItem>
                    <LabeledListItem data-test-id="createdOn" label={lt('CreatedOn')}>
                        <LocalizedDateTimeFromUtc utcString={createdUtc} />
                    </LabeledListItem>
                    <LabeledListItem data-test-id="updatedBy" label={lt('UpdatedBy')} itemAlignment="center">
                        <Stack itemAlignment="center" spacing="200">
                            <Avatar src={updaterImgSrc} size="medium" title={updaterProfile?.name} />
                            {updaterProfile?.name}
                        </Stack>
                    </LabeledListItem>
                    <LabeledListItem data-test-id="updatedOn" label={lt('UpdatedOn')}>
                        <LocalizedDateTimeFromUtc utcString={updatedUtc} />
                    </LabeledListItem>
                </DividerList>
            </ContentSection>
            {renderEditProductDetailsForm()}
        </>
    );
};
